import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useElapsedTime } from '@/helpers/time'
import { DeleteServerDialog } from './DeleteServerDialog'
import { useRunningCostDisplay } from '@/hooks/useRunningCostDisplay'
import { cn } from '@/lib/utils'
import {
  Server,
  ExternalLink,
  Cpu,
  Microchip,
  Fan,
  Activity,
} from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { ListServerInstanceItem } from '@/services/inference.gen'
import { UptimeBadge } from '@/components/UptimeBadge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ExecutionDetails } from '@/components/ExecutionDetails'

type ServerCardProps = {
  metadata: ListServerInstanceItem
  onViewDetails?: () => void
}

export const ServerCard = ({ metadata, onViewDetails }: ServerCardProps) => {
  const {
    started_at,
    created_at,
    status,
    instance_name,
    template_name,
    user_metadata,
    price,
    email,
    instance_id,
    template,
    terminal_exit_code,
    terminal_finished_at,
    terminal_reason,
    terminal_message,
    restart_count,
    enable_scale,
    instance_scale_config,
  } = metadata

  const cpu_count = template?.cpu_limit_count
  const ram_gb = template?.ram_limit_gb
  const gpu_request_count = template?.gpu_request_count
  const gpu_request_gmem = template?.gpu_request_gmem

  // Use created_at for transitional servers, started_at for running servers
  const isRunningServer = status.toLowerCase() === 'running'
  const isPendingServer = status.toLowerCase() === 'pending'
  const timestampForUptime = isRunningServer ? started_at : created_at
  const uptime = useElapsedTime(timestampForUptime)

  const runningCostDollars = useRunningCostDisplay({
    started_at: started_at, // Only calculate cost from when server actually started
    hourly_price_cents: price,
  })

  const priceInDollars = price / 100

  return (
    <div
      className={cn(
        'server-card',
        'p-6 rounded-lg shadow-sm border transition-all duration-200 hover:shadow-md group',
        'bg-card border-border',
        isRunningServer && 'border-primary border-2'
      )}
      data-testid={`server-card-${instance_id}`}
    >
      <div className="flex justify-between items-start">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-primary/70" />
            <h3
              className="text-2xl font-bold"
              data-testid={`server-name-${instance_id}`}
            >
              {instance_name || 'Unknown'}
            </h3>
          </div>

          <div className="flex items-center gap-2">
            <UptimeBadge
              status={status}
              uptime={uptime}
              variant={
                status === 'running'
                  ? 'running'
                  : status === 'stopped'
                    ? 'stopped'
                    : 'default'
              }
              data-testid={`server-status-${instance_id}`}
            />

            {enable_scale && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="secondary"
                      className="flex items-center gap-1 bg-primary/10 text-primary border-primary/20"
                      data-testid={`server-autoscaling-badge-${instance_id}`}
                    >
                      <Activity className="w-3 h-3" />
                      Autoscaling
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    {instance_scale_config ? (
                      <div className="space-y-1 text-xs">
                        <p>
                          Min: {instance_scale_config.min_replica_count} | Max:{' '}
                          {instance_scale_config.max_replica_count}
                        </p>
                        <p>
                          Polling: {instance_scale_config.polling_interval}s |
                          Cooldown: {instance_scale_config.cooldown_period}s
                        </p>
                      </div>
                    ) : (
                      <p>Autoscaling enabled</p>
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        <div className="text-right">
          <div
            className="text-2xl font-bold text-primary"
            data-testid={`server-price-${instance_id}`}
          >
            ${priceInDollars.toFixed(2)}/hr
          </div>
          {isRunningServer && (
            <div
              className="text-sm text-foreground/60"
              data-testid={`server-cost-${instance_id}`}
            >
              Cost: ${runningCostDollars}
            </div>
          )}
        </div>
      </div>

      <div className="mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="flex items-center gap-2"
                  data-testid={`server-cpu-${instance_id}`}
                >
                  <Cpu className="w-4 h-4 text-primary/70" />
                  <span className="text-sm font-medium">
                    {cpu_count || 'N/A'} CPU
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>CPU Cores</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="flex items-center gap-2"
                  data-testid={`server-ram-${instance_id}`}
                >
                  <Microchip className="w-4 h-4 text-primary/70" />
                  <span className="text-sm font-medium">
                    {ram_gb || 'N/A'} GB RAM
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Memory (RAM)</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {gpu_request_count && gpu_request_count > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="flex items-center gap-2"
                    data-testid={`server-gpu-${instance_id}`}
                  >
                    <Fan className="w-4 h-4 text-primary/70" />
                    <span className="text-sm font-medium">
                      {gpu_request_count} GPU
                      {gpu_request_gmem ? ` (${gpu_request_gmem}GB)` : ''}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    Graphics Processing Unit
                    {gpu_request_gmem
                      ? ` with ${gpu_request_gmem}GB memory`
                      : ''}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      <Separator className="my-4" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="col-span-2 space-y-2 text-sm">
          <div className="flex gap-2">
            <span className="text-foreground/60 w-24">Template:</span>
            <span
              className="font-medium"
              data-testid={`server-template-${instance_id}`}
            >
              {template_name || 'Unknown'}
            </span>
          </div>

          <div className="flex gap-2">
            <span className="text-foreground/60 w-24">Created By:</span>
            <span
              className="font-medium"
              data-testid={`server-owner-${instance_id}`}
            >
              {email}
              {user_metadata?.first_name && user_metadata?.last_name
                ? ` (${user_metadata.first_name} ${user_metadata.last_name})`
                : ''}
            </span>
          </div>

          <ExecutionDetails
            terminal_reason={terminal_reason}
            terminal_message={terminal_message}
            restart_count={restart_count}
            terminal_exit_code={terminal_exit_code}
            terminal_finished_at={terminal_finished_at}
          />
        </div>

        <div className="flex flex-col justify-end gap-2">
          <div className="flex items-center gap-2 justify-end mt-auto">
            <Button
              variant="default"
              className="flex items-center gap-1.5"
              onClick={onViewDetails}
              disabled={isPendingServer}
              data-testid={`server-details-button-${instance_id}`}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Show Details
            </Button>
            <DeleteServerDialog
              instanceId={instance_id}
              serverName={instance_name}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
